import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./TranslationsManager.module.css";
import { useLanguage } from "../../context/LanguageContext";
import { useIconPack } from "../../context/IconPackContext";
import { translationApi, TranslationRow } from "../../helper/translations";
import { modulesApi } from "../../helper/modules";
import { ToastService } from "../../service/toast.service";
import { migrateTranslationsFromSeed } from "../../scripts/migrateTranslations";
import SearchableDropdown from "../../components/SearchableDropdown/SearchableDropdown";

const ITEMS_PER_PAGE = 15;

export default function TranslationsManager() {
  const { t, reloadTranslations } = useLanguage();
  const { getIcon } = useIconPack();
  const navigate = useNavigate();

  const [isMigrating, setIsMigrating] = useState(false);
  const [translations, setTranslations] = useState<TranslationRow[]>([]);
  const [modules, setModules] = useState<Array<{ id: number; name: string }>>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<TranslationRow>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationCount, setMigrationCount] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [newTranslation, setNewTranslation] = useState<Partial<TranslationRow>>(
    {
      key: "",
      module: null,
      english: "",
      russian: "",
      ukrainian: "",
    },
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await translationApi.getAll();
      setTranslations(data);
      setCurrentPage(1);
    } catch (error) {
      ToastService.error(t("error") + ": Failed to load translations");
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async () => {
    try {
      const allModules = await modulesApi.getAll();
      setModules(allModules);
    } catch (error) {
      console.error("Failed to load modules:", error);
    }
  };

  const handleMigrateFromSeed = async () => {
    if (!window.confirm("Import translations from seed into database?")) return;

    setIsMigrating(true);
    try {
      const result = await migrateTranslationsFromSeed(migrationCount);

      ToastService.success(
        `Import complete: ${result.migrated} new, ${result.updated} updated, ${result.failed} failed`,
      );

      await loadData();
      await reloadTranslations();
      setShowMigrationModal(false);
    } catch (error) {
      console.error("Migration error:", error);
      ToastService.error("Import failed");
    } finally {
      setIsMigrating(false);
    }
  };

  useEffect(() => {
    loadData();
    loadModules();
  }, []);

  const handleEdit = (row: TranslationRow) => {
    setEditingId(row.id);
    setFormData(row);
  };

  const handleSave = async () => {
    if (!editingId) return;

    try {
      await translationApi.update(editingId, formData);
      ToastService.success(t("save") + " successful!");
      await loadData();
      await reloadTranslations();
      setEditingId(null);
      setFormData({});
    } catch (error) {
      ToastService.error(t("error") + ": Failed to save translation");
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t("confirmDelete"))) return;

    try {
      await translationApi.delete(id);
      ToastService.success(t("delete") + " successful!");
      await loadData();
      await reloadTranslations();
    } catch (error) {
      ToastService.error(t("error") + ": Failed to delete translation");
    }
  };

  const handleAdd = async () => {
    if (!newTranslation.key) {
      ToastService.error("Key is required");
      return;
    }

    try {
      await translationApi.create(newTranslation as Omit<TranslationRow, "id">);
      ToastService.success("Translation added successfully!");
      await loadData();
      await reloadTranslations();
      setShowAddForm(false);
      setNewTranslation({
        key: "",
        module: null,
        english: "",
        russian: "",
        ukrainian: "",
      });
    } catch (error) {
      ToastService.error(t("error") + ": Failed to add translation");
    }
  };

  const filteredTranslations = translations.filter((row) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      row.key?.toLowerCase().includes(query) ||
      row.english?.toLowerCase().includes(query) ||
      row.russian?.toLowerCase().includes(query) ||
      row.ukrainian?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(filteredTranslations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTranslations = filteredTranslations.slice(
    startIndex,
    endIndex,
  );

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const moduleOptions = [
    { label: "-- Global (No Module) --", value: "" },
    ...modules.map((mod) => ({
      label: `${mod.name} (ID: ${mod.id})`,
      value: mod.name,
    })),
  ];

  const moduleOptionsShort = [
    { label: "-- Global --", value: "" },
    ...modules.map((mod) => ({
      label: mod.name,
      value: mod.name,
    })),
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t("loading")}</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button onClick={() => navigate(-1)} className={styles.backButton}>
          <FontAwesomeIcon icon={getIcon("arrow-left")} />
          {t("back")}
        </button>

        <h1 className={styles.title}>{t("translateTitle")}</h1>

        <div className={styles.headerButtons}>
          <button
            onClick={() => setShowMigrationModal(true)}
            className={`${styles.addButton} ${styles.importButton}`}
            disabled={isMigrating}
          >
            <FontAwesomeIcon icon={getIcon("database")} />
            {t("impSeed")}
          </button>

          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className={styles.addButton}
          >
            <FontAwesomeIcon icon={getIcon("plus")} />
            {t("add")}
          </button>
        </div>
      </div>

      {showMigrationModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>{t("migrateTranslations")}</h3>{" "}
            <p className={styles.modalText}>{t("chooseMigrationCount")}</p>
            <input
              type="number"
              min="1"
              max="1000"
              value={migrationCount}
              onChange={(e) => setMigrationCount(Number(e.target.value))}
              disabled={isMigrating}
              className={styles.modalInput}
            />
            <div className={styles.modalButtons}>
              <button
                onClick={handleMigrateFromSeed}
                disabled={isMigrating}
                className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
              >
                {isMigrating ? t("importingStatus") : t("importAction")}
              </button>

              <button
                onClick={() => setShowMigrationModal(false)}
                disabled={isMigrating}
                className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.searchBar}>
        <input
          type="text"
          placeholder={t("searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className={styles.searchInput}
        />
        <span className={styles.searchIcon}>
          <FontAwesomeIcon icon={getIcon("search")} />
        </span>
      </div>

      {showAddForm && (
        <div className={styles.addForm}>
          <h3>{t('addNewTranslation')}</h3>
          <div className={styles.formRow}>
            <input
              type="text"
              placeholder={t('keyLabel')}
              value={newTranslation.key || ""}
              onChange={(e) =>
                setNewTranslation({ ...newTranslation, key: e.target.value })
              }
              className={styles.input}
            />

            <SearchableDropdown
              options={moduleOptionsShort}
              value={newTranslation.module || ""}
              onChange={(value) =>
                setNewTranslation({
                  ...newTranslation,
                  module: value || null,
                })
              }
            />

            <input
              type="text"
              placeholder="English"
              value={newTranslation.english || ""}
              onChange={(e) =>
                setNewTranslation({
                  ...newTranslation,
                  english: e.target.value,
                })
              }
              className={styles.input}
            />
            <input
              type="text"
              placeholder="Русский"
              value={newTranslation.russian || ""}
              onChange={(e) =>
                setNewTranslation({
                  ...newTranslation,
                  russian: e.target.value,
                })
              }
              className={styles.input}
            />
            <input
              type="text"
              placeholder="Українська"
              value={newTranslation.ukrainian || ""}
              onChange={(e) =>
                setNewTranslation({
                  ...newTranslation,
                  ukrainian: e.target.value,
                })
              }
              className={styles.input}
            />
            <button onClick={handleAdd} className={styles.saveBtn}>
              <FontAwesomeIcon icon={getIcon("check")} />
            </button>
            <button
              onClick={() => setShowAddForm(false)}
              className={styles.cancelBtn}
            >
              <FontAwesomeIcon icon={getIcon("xmark")} />
            </button>
          </div>
        </div>
      )}

      <div className={styles.stats}>
        <span>
          {t("total")}: {translations.length}
        </span>{" "}
        <span>
          {t("filtered")}: {filteredTranslations.length}
        </span>{" "}
        <span>
          {t("page")}: {currentPage} / {totalPages}
        </span>
        <span>
          {t("showing")}: {startIndex + 1}-
          {Math.min(endIndex, filteredTranslations.length)}
        </span>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Key</th>
              <th>Module</th>
              <th>English</th>
              <th>Russian</th>
              <th>Ukrainian</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTranslations.map((row) => (
              <tr
                key={row.id}
                className={editingId === row.id ? styles.editing : ""}
              >
                <td>{row.id}</td>
                <td>
                  {editingId === row.id ? (
                    <input
                      value={formData.key || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, key: e.target.value })
                      }
                      className={styles.input}
                    />
                  ) : (
                    <code>{row.key}</code>
                  )}
                </td>
                <td>
                  {editingId === row.id ? (
                    <input
                      type="text"
                      placeholder="Module (optional)"
                      value={formData.module || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          module: e.target.value || null,
                        })
                      }
                      className={styles.input}
                    />
                  ) : (
                    <span>{row.module || "Global"}</span>
                  )}
                </td>
                <td>
                  {editingId === row.id ? (
                    <textarea
                      value={formData.english || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, english: e.target.value })
                      }
                      className={styles.textarea}
                      rows={2}
                    />
                  ) : (
                    row.english
                  )}
                </td>
                <td>
                  {editingId === row.id ? (
                    <textarea
                      value={formData.russian || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, russian: e.target.value })
                      }
                      className={styles.textarea}
                      rows={2}
                    />
                  ) : (
                    row.russian
                  )}
                </td>
                <td>
                  {editingId === row.id ? (
                    <textarea
                      value={formData.ukrainian || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, ukrainian: e.target.value })
                      }
                      className={styles.textarea}
                      rows={2}
                    />
                  ) : (
                    row.ukrainian
                  )}
                </td>
                <td>
                  <div className={styles.actions}>
                    {editingId === row.id ? (
                      <>
                        <button onClick={handleSave} className={styles.saveBtn}>
                          <FontAwesomeIcon icon={getIcon("check")} />
                        </button>
                        <button
                          onClick={handleCancel}
                          className={styles.cancelBtn}
                        >
                          <FontAwesomeIcon icon={getIcon("xmark")} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(row)}
                          className={styles.editBtn}
                        >
                          <FontAwesomeIcon icon={getIcon("pen")} />
                        </button>
                        <button
                          onClick={() => handleDelete(row.id)}
                          className={styles.deleteBtn}
                        >
                          <FontAwesomeIcon icon={getIcon("trash")} />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          className={styles.paginationBtn}
        >
          <FontAwesomeIcon icon={getIcon("angles-left")} />
        </button>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className={styles.paginationBtn}
        >
          <FontAwesomeIcon icon={getIcon("angle-left")} />
        </button>

        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= 5) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
          } else if (currentPage >= totalPages - 2) {
            pageNum = totalPages - 4 + i;
          } else {
            pageNum = currentPage - 2 + i;
          }

          return (
            <button
              key={pageNum}
              onClick={() => goToPage(pageNum)}
              className={`${styles.paginationBtn} ${currentPage === pageNum ? styles.active : ""}`}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={styles.paginationBtn}
        >
          <FontAwesomeIcon icon={getIcon("angle-right")} />
        </button>
        <button
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          className={styles.paginationBtn}
        >
          <FontAwesomeIcon icon={getIcon("angles-right")} />
        </button>
      </div>
    </div>
  );
}
